/**
 * CommissionSplitsCard — the adviser commission split block on File Detail
 * (decomposition step F6a, CLAUDE.md §8.4).
 *
 * Owns the editor state that only it reads (`splitsEditing`, `splitsDraft`,
 * `splitsDraftValid`), the Save / Cancel / Edit handlers and the
 * COMMISSION_LOCKED_STATUSES rule. The write path (`updateFile`) stays as
 * it was in the view: it moved here because nothing else in FileDetail
 * called it. The view passes the file, its id, the shared `actionLoading`
 * flag and its setters (`setActionLoading`, `setFile`, `setError`).
 */

import React, { useState } from 'react';
import { Pencil, Lock } from 'lucide-react';
import { updateFile } from '../../api/filesApi';
import CommissionSplitEditor, {
  defaultCommissionSplits,
  isCommissionSplitsValid,
} from './CommissionSplitEditor';
import { sharedStyles } from './FileDetail.styles';

// Statuses at which the adviser commission split is sealed and may no
// longer be edited. The brief locks the split from Awaiting Signature
// onwards because that is the moment the file commits to going out for
// the client's signature; the percentages can no longer move after that.
const COMMISSION_LOCKED_STATUSES = new Set(['sent_to_sign', 'signed', 'completed']);

export default function CommissionSplitsCard({
  file,
  id,
  actionLoading,
  setActionLoading,
  setFile,
  setError,
}) {
  // Adviser commission split editor state.
  // `splitsEditing` toggles between the read-only display and the inline
  // CommissionSplitEditor. `splitsDraft` holds the in-progress edit so the
  // user can Cancel without dirtying the saved value, and `splitsDraftValid`
  // mirrors the editor's `isValid` flag so Save can be gated.
  const [splitsEditing, setSplitsEditing] = useState(false);
  const [splitsDraft, setSplitsDraft] = useState(null);
  const [splitsDraftValid, setSplitsDraftValid] = useState(true);

  // Adviser commission split — Save / Cancel handlers.
  // The editor is shown inline next to the read-only display; Save
  // persists the draft to Supabase via updateFile, and Cancel discards
  // the draft and reverts to the saved value.
  const handleSaveCommissionSplits = async () => {
    if (!splitsDraft || !isCommissionSplitsValid(splitsDraft)) return;
    try {
      setActionLoading(true);
      // Coerce shape (trim names, force percent integers) before persisting.
      const cleaned = splitsDraft.map((row) => ({
        name: (row.name || '').trim(),
        percent: parseInt(row.percent, 10) || 0,
      }));
      const updated = await updateFile(id, { commissionSplits: cleaned });
      setFile(updated);
      setSplitsEditing(false);
      setSplitsDraft(null);
    } catch (err) {
      console.error('Failed to save commission splits:', err);
      setError(err.message || 'Failed to save commission splits.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelCommissionSplits = () => {
    setSplitsEditing(false);
    setSplitsDraft(null);
    setSplitsDraftValid(true);
  };

  const handleStartEditCommissionSplits = () => {
    // Seed the draft from the file's current splits, falling back to the
    // default 70 / 30 if the row predates the migration somehow.
    const seed =
      Array.isArray(file?.commissionSplits) && file.commissionSplits.length > 0
        ? file.commissionSplits.map((row) => ({ ...row }))
        : defaultCommissionSplits();
    setSplitsDraft(seed);
    setSplitsDraftValid(isCommissionSplitsValid(seed));
    setSplitsEditing(true);
  };

  return (
    <>
          {(() => {
            const locked = COMMISSION_LOCKED_STATUSES.has(file.status);
            const splits = Array.isArray(file.commissionSplits) && file.commissionSplits.length > 0
              ? file.commissionSplits
              : defaultCommissionSplits();
            return (
              <div style={styles.splitSection}>
                <div style={styles.splitHeaderRow}>
                  <span style={styles.metaLabel}>Adviser Commission Split</span>
                  {!locked && !splitsEditing && (
                    <button
                      type="button"
                      style={styles.editCasesBtn}
                      onClick={handleStartEditCommissionSplits}
                      disabled={actionLoading}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                </div>

                {splitsEditing ? (
                  <>
                    <CommissionSplitEditor
                      value={splitsDraft || splits}
                      onChange={setSplitsDraft}
                      onValidityChange={setSplitsDraftValid}
                      disabled={actionLoading}
                    />
                    <div style={styles.splitEditFooter}>
                      <button
                        className="btn btn-outline"
                        type="button"
                        onClick={handleCancelCommissionSplits}
                        disabled={actionLoading}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={handleSaveCommissionSplits}
                        disabled={actionLoading || !splitsDraftValid}
                        title={!splitsDraftValid ? 'Splits must total 100% with every row named.' : undefined}
                      >
                        {actionLoading ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={styles.splitReadOnly}>
                      {splits.map((row, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && <span style={styles.splitDivider}>|</span>}
                          <span style={styles.splitItem}>
                            <strong>{row.name}</strong> — {row.percent}%
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                    {locked && (
                      <div style={styles.splitLockHint}>
                        <Lock size={11} />
                        <span>Locked at signature stage</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}
    </>
  );
}

export const styles = {
  splitSection: {
    marginBottom: '1.5rem',
    padding: '0.85rem 1rem',
    background: 'var(--cp-bg)',
    borderRadius: '10px',
  },
  splitHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    marginBottom: '0.5rem',
  },
  splitReadOnly: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.4rem 0.6rem',
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
  },
  splitItem: {
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
  },
  splitDivider: {
    color: '#9FB1C2',
    fontWeight: 400,
  },
  splitLockHint: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    marginTop: '0.45rem',
    fontSize: '0.75rem',
    color: 'var(--cp-text-muted)',
    fontStyle: 'italic',
  },
  splitEditFooter: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '0.75rem',
  },
  // Shared with the FileDetail view (F7 seed, `FileDetail.styles.js`).
  metaLabel: sharedStyles.metaLabel,
  editCasesBtn: sharedStyles.editCasesBtn,
};
